import posthog from 'posthog-js'
import { jwtDecode } from 'jwt-decode'

import { hasCookieConsent } from '../hooks/useCookieConsent/useCookieConsent'

import { registerPostHogCapture } from './trackingService'

/**
 * PostHog product analytics (Event Tracking contract).
 *
 * Starts in cookieless mode so undecided and rejecting visitors are still
 * measured, then upgrades in place on acceptance. Provides session replay +
 * native exception autocapture (`$exception`) and wires PostHog into the
 * central tracking service so canonical events fan out with per-event
 * order/outlet/marketplace group association.
 */

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com'

let initialised = false

/**
 * Initialises PostHog and registers it with the tracking service. No-op without a key.
 *
 * Safe to call before the customer has answered the cookie banner: capture
 * starts opted out, and `cookieless_mode: 'on_reject'` means opted-out traffic
 * is still measured against a server-side hash rather than dropped.
 *
 * Requires cookieless mode to be enabled in the PostHog project settings,
 * otherwise the cookieless events are ingested and discarded.
 */
export const initPostHog = (): void => {
  if (initialised || !POSTHOG_KEY) return
  initialised = true

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Data minimisation: only create person profiles once a customer identifies.
    person_profiles: 'identified_only',
    // Native crash autocapture -> `$exception` (per the Event Tracking contract).
    capture_exceptions: true,
    autocapture: true,
    // SPA: without this only the first load records a $pageview, not route changes.
    capture_pageview: 'history_change',
    opt_out_capturing_by_default: true,
    cookieless_mode: 'on_reject',
    // Replay is too invasive to run pre-consent; started on acceptance instead.
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
    },
  })

  registerPostHogCapture((event, properties, groups) => {
    posthog.capture(
      event,
      groups ? { ...properties, $groups: groups } : properties
    )
  })
}

/**
 * Associates subsequent events with the logged-in customer.
 *
 * Gated on explicit acceptance: naming a customer is the one thing cookieless
 * mode cannot make anonymous.
 */
export const identifyPostHog = (customerId: string): void => {
  if (!initialised || !hasCookieConsent()) return
  posthog.identify(customerId)
}

/**
 * Identifies the customer from a JWT after login/register. PostHog aliases the
 * prior anonymous distinct_id to this customer, so earlier same-session events
 * are retro-attributed too.
 */
export const identifyPostHogFromToken = (token?: string | null): void => {
  if (!token) return
  try {
    const { customer_id: customerId } = jwtDecode<{ customer_id: string }>(
      token
    )
    if (customerId) identifyPostHog(customerId)
  } catch {
    // malformed token - skip identify
  }
}

type MarketplaceGroupProps = {
  name?: string
  host?: string
  partner_id?: string
  platform_id?: string
}
type PartnerGroupProps = { name?: string; platform_id?: string }
type PlatformGroupProps = { name?: string }

// Remembered so we can re-attach tenant groups after reset() (which clears them).
let lastMarketplaceGroup: {
  id: string
  properties?: MarketplaceGroupProps
} | null = null
let lastPartnerGroup: { id: string; properties?: PartnerGroupProps } | null =
  null
let lastPlatformGroup: { id: string; properties?: PlatformGroupProps } | null =
  null

/**
 * Associates ALL subsequent events (including auto-captured pageviews, clicks,
 * session replays and exceptions) with a marketplace group.
 *
 * This is what powers per-marketplace website analytics and the platform-wide
 * rollup: setting the group persistently means every event is tagged with
 * `$groups: { marketplace }`, not just the manually tracked canonical events.
 * `partner_id` is stored as a group property so marketplaces roll up to their
 * partner without needing the partner group on every breakdown.
 */
export const setPostHogMarketplaceGroup = (
  marketplaceId: string,
  properties?: MarketplaceGroupProps
): void => {
  lastMarketplaceGroup = { id: marketplaceId, properties }
  if (!initialised) return
  posthog.group('marketplace', marketplaceId, properties)
}

/**
 * Associates all subsequent events with a partner group. Partner sits under
 * platform in the RedBox hierarchy (Platform → Partner → Marketplace/Business
 * → Outlet). Enables the partner-level rollup of website traffic.
 */
export const setPostHogPartnerGroup = (
  partnerId: string,
  properties?: PartnerGroupProps
): void => {
  lastPartnerGroup = { id: partnerId, properties }
  if (!initialised) return
  posthog.group('partner', partnerId, properties)
}

/**
 * Associates all subsequent events with a platform group — the top of the
 * RedBox hierarchy and the currency boundary (one platform = one currency).
 */
export const setPostHogPlatformGroup = (
  platformId: string,
  properties?: PlatformGroupProps
): void => {
  lastPlatformGroup = { id: platformId, properties }
  if (!initialised) return
  posthog.group('platform', platformId, properties)
}

/** Clears the current identity on logout, keeping the tenant group association. */
export const resetPostHog = (): void => {
  if (!initialised) return
  posthog.reset()
  // reset() also clears group associations, so re-attach the tenant groups.
  if (lastMarketplaceGroup) {
    posthog.group(
      'marketplace',
      lastMarketplaceGroup.id,
      lastMarketplaceGroup.properties
    )
  }
  if (lastPartnerGroup) {
    posthog.group('partner', lastPartnerGroup.id, lastPartnerGroup.properties)
  }
  if (lastPlatformGroup) {
    posthog.group(
      'platform',
      lastPlatformGroup.id,
      lastPlatformGroup.properties
    )
  }
}

/** Upgrades cookieless capture to full capture with storage and replay. */
export const grantPostHogConsent = (): void => {
  if (!initialised) return
  posthog.opt_in_capturing()
  posthog.startSessionRecording()
}

/**
 * Drops back to cookieless capture: no storage, no replay, no identity.
 *
 * Capture is deliberately left registered — opted-out traffic still flows,
 * anonymously, via `cookieless_mode`.
 */
export const revokePostHogConsent = (): void => {
  if (!initialised) return
  posthog.stopSessionRecording()
  posthog.opt_out_capturing()
  resetPostHog()
}
